/* eslint-disable react/no-danger */
import React, { useMemo } from 'react';

import { SmButton } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';

import useEvent from '@/hooks/useEvent';
import useApi from '@/hooks/useApi';

import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TVoucherItem } from '@/services/requests/Voucher/types';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { getUuid } from '@/helpers/StringHelper/uuid';
import { dateStringFormat } from '@/helpers/StringHelper/dateStringFormat';
import { numberToBrl } from '@/helpers/StringHelper/numberToBrl';
import { getPartnerLabel } from '@/helpers/PartnerRulesHelper';
import { valueFallback } from '@/helpers/ValueFallbackHelper';

type TProps = {
  onClose: () => void;
  liferayContent: TModalContent;
  tripType?: boolean;
  currentVoucher: TVoucherItem;
};

export const AirlineVoucherRules = ({
  onClose,
  liferayContent,
  currentVoucher,
}: TProps) => {
  const liferayFilled = useMemo<TModalContent>(() => {
    return JSON.parse(
      Mustache.render(JSON.stringify(liferayContent), {
        voucher: currentVoucher,
      }),
    );
  }, [currentVoucher, liferayContent]);

  const classRuleTypeLabel = {
    ALL: liferayContent.allClassTypeLabel?.text,
    COMFORT: liferayContent.comfortLabel?.text,
    BUSINESS: liferayContent.businessLabel?.text,
  };

  const travelClassKey = (
    currentVoucher.travelClassesList?.[0]?.travelClass || 'ALL'
  ).toUpperCase() as keyof typeof classRuleTypeLabel;

  const tripType = currentVoucher.tripTypesList?.[0]?.tripType?.toLowerCase();
  let tripTypeLabel = '';

  if (tripType && tripType === 'all') {
    tripTypeLabel = valueFallback(liferayContent?.allTripLabel?.text, '');
  } else if (
    tripType &&
    (tripType === 'one_way' || tripType === 'one way' || tripType === 'oneway')
  ) {
    tripTypeLabel = valueFallback(liferayContent.oneWayLabel?.text, '');
  } else {
    tripTypeLabel = valueFallback(liferayContent.roundtripLabel?.text, '');
  }

  const voucherRules = [
    { ...liferayFilled.codeLabel, value: currentVoucher.voucherNumber },
    {
      ...liferayFilled.validityLabel,
      value: `${clearHtmlTags(
        liferayFilled.untilLabel?.text || '',
      )} ${dateStringFormat(currentVoucher.expiration)}`,
    },
    {
      ...liferayFilled.discountMilesLabel,
      value: valueFallback(
        currentVoucher.quantity.toLocaleString('pt-BR'),
        '0',
      ),
    },
    {
      ...liferayFilled.discountLabel,
      value: `${valueFallback(currentVoucher.discount, 0)}%`,
    },
    {
      ...liferayFilled.discountMoneyLabel,
      value: currentVoucher.moneyVoucher
        ? numberToBrl(Number(currentVoucher.moneyVoucher), true)
        : 'R$ 0,00',
    },
    {
      ...liferayFilled.discountPercentageMoneyLabel,
      value: `${valueFallback(currentVoucher.moneyVoucherPercentage, 0)}%`,
    },
    {
      ...liferayFilled.tripTypeLabel,
      value: tripTypeLabel,
    },
    {
      ...liferayFilled.classTypeLabel,
      value: classRuleTypeLabel[travelClassKey] ?? 'ALL',
    },
    {
      ...liferayFilled.termOfUseLabel,
      value: valueFallback(
        currentVoucher?.flightPeriodsList?.[0]?.flightPeriod?.[0]?.replace(
          /\[|\]/g,
          '',
        ),
        '',
      ),
    },
    {
      ...liferayFilled.odLabel,
      value: currentVoucher.airportsList?.[0]?.originDestinationAirport || '',
    },
    {
      ...liferayFilled.reusableLabel,
      value: currentVoucher.reusable
        ? liferayContent.reusableYesValue?.text
        : liferayContent.reusableNoValue?.text,
    },
    {
      ...liferayFilled.partnerCompanyNameLabel,
      value: getPartnerLabel(
        valueFallback(currentVoucher.partnerAlias, ''),
        valueFallback(currentVoucher.partnerList, []),
      ),
    },
    {
      ...liferayFilled.paymentTypeLabel,
      value: currentVoucher.paymentType,
    },
    {
      ...liferayFilled.productTypeLabel,
      value: currentVoucher.productType,
    },
  ];

  const { ApiInteractions } = useApi();
  const event = useEvent();

  const handleClose = (): void => {
    dispatchInteraction(ApiInteractions, 'extra_av_rules_click_close', {
      recommendationId: event.data.recommendationId,
    });

    onClose();
  };

  return (
    <div className="smls-b2c-modals__modal-content">
      <div className="smls-b2c-modals__button--close">
        <SmButton
          onClick={handleClose}
          isPill
          isOutlined
          icon={<span className="material-icons">close</span>}
        />
      </div>
      <div className="rules">
        {liferayFilled.title && (
          <div
            className="rules__title"
            dangerouslySetInnerHTML={{ __html: liferayFilled.title.text }}
          />
        )}
        <div className="rules__table">
          {voucherRules.map(({ icon, text, value }) => (
            <div className="rules__line" key={getUuid()}>
              {icon && (
                <div className="rules__icon">
                  <img src={icon} alt={clearHtmlTags(text ?? '')} />
                </div>
              )}

              {text && (
                <div
                  className="rules__text"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              )}
              {value && (
                <div
                  className="rules__value"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
